import React, { useRef, useEffect, useState, useCallback } from 'react'
import { Camera, X, Keyboard, AlertCircle, CheckCircle2, Loader2 } from 'lucide-react'
import { Html5Qrcode, Html5QrcodeScannerState } from 'html5-qrcode'

interface BarcodeScannerProps {
  onScan: (barcode: string) => void
  onClose: () => void
  isLoading?: boolean
}

export const BarcodeScanner: React.FC<BarcodeScannerProps> = ({ 
  onScan, 
  onClose,
  isLoading = false 
}) => {
  const [isScanning, setIsScanning] = useState(false)
  const [manualBarcode, setManualBarcode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [lastScannedCode, setLastScannedCode] = useState<string | null>(null)
  const [scannerStatus, setScannerStatus] = useState<'idle' | 'initializing' | 'scanning' | 'success' | 'error'>('idle')
  
  const scannerRef = useRef<Html5Qrcode | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const stopCamera = useCallback(async () => {
    if (scannerRef.current) {
      try {
        const state = scannerRef.current.getState()
        if (state === Html5QrcodeScannerState.SCANNING) {
          await scannerRef.current.stop()
        }
      } catch (err) {
        console.warn('Error stopping scanner:', err)
      }
      scannerRef.current = null
    }
    setIsScanning(false)
    setScannerStatus('idle')
  }, [])

  const startCamera = async () => {
    setError(null)
    setScannerStatus('initializing')

    try {
      // Check for camera permissions
      const devices = await Html5Qrcode.getCameras()
      
      if (devices.length === 0) {
        throw new Error('No cameras found on this device')
      }

      // Prefer back camera for scanning
      const backCamera = devices.find(d => 
        d.label.toLowerCase().includes('back') || 
        d.label.toLowerCase().includes('rear') ||
        d.label.toLowerCase().includes('environment')
      )
      const cameraId = backCamera?.id || devices[0].id

      // Create scanner instance
      scannerRef.current = new Html5Qrcode('barcode-scanner-container')

      await scannerRef.current.start(
        cameraId,
        {
          fps: 10,
          qrbox: { width: 250, height: 150 },
          aspectRatio: 1.0,
        },
        (decodedText) => {
          // Successfully scanned
          setLastScannedCode(decodedText)
          setScannerStatus('success')
          
          // Play success sound (optional)
          try {
            const audio = new Audio('data:audio/wav;base64,UklGRl9vT19XQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YU')
            audio.volume = 0.3
            audio.play().catch(() => {})
          } catch {}

          // Callback and close
          setTimeout(() => {
            onScan(decodedText)
            stopCamera()
          }, 500)
        },
        () => {
          // QR code not detected (ignore - this fires constantly)
        }
      )

      setIsScanning(true)
      setScannerStatus('scanning')
    } catch (err: any) {
      console.error('Error starting camera:', err)
      setScannerStatus('error')
      
      if (err.message?.includes('NotAllowedError') || err.message?.includes('Permission')) {
        setError('Camera permission denied. Please allow camera access and try again.')
      } else if (err.message?.includes('NotFoundError') || err.message?.includes('No cameras')) {
        setError('No camera found. Please use manual entry.')
      } else {
        setError(err.message || 'Unable to start camera. Please use manual entry.')
      }
    }
  }

  const handleManualScan = () => {
    const barcode = manualBarcode.trim()
    if (barcode.length >= 3) {
      setLastScannedCode(barcode)
      setScannerStatus('success')
      setTimeout(() => {
        onScan(barcode)
        setManualBarcode('')
      }, 300)
    }
  }

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopCamera()
    }
  }, [stopCamera])

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        stopCamera()
        onClose()
      }
    }
    window.addEventListener('keydown', handleEscape)
    return () => window.removeEventListener('keydown', handleEscape)
  }, [onClose, stopCamera])

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="glass-card max-w-md w-full border border-border/50">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h3 className="text-xl font-bold esales-text">Barcode Scanner</h3>
            <p className="text-xs text-muted-foreground mt-1">Scan product barcode or ISBN</p>
          </div>
          <button
            onClick={() => {
              stopCamera()
              onClose()
            }}
            className="p-2 rounded-lg hover:bg-muted transition-colors"
            disabled={isLoading}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Loading Overlay */}
        {isLoading && (
          <div className="absolute inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center z-10 rounded-xl">
            <div className="text-center">
              <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">Looking up product...</p>
            </div>
          </div>
        )}

        {/* Scanner Container */}
        {isScanning ? (
          <div className="space-y-4">
            <div className="relative bg-black rounded-lg overflow-hidden">
              <div 
                id="barcode-scanner-container" 
                ref={containerRef}
                className="w-full aspect-square"
              />
              
              {/* Scanning Overlay */}
              {scannerStatus === 'scanning' && (
                <div className="absolute inset-0 pointer-events-none">
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-64 h-32 border-2 border-primary rounded relative">
                      <div className="absolute -top-0.5 -left-0.5 w-6 h-6 border-t-4 border-l-4 border-primary rounded-tl" />
                      <div className="absolute -top-0.5 -right-0.5 w-6 h-6 border-t-4 border-r-4 border-primary rounded-tr" />
                      <div className="absolute -bottom-0.5 -left-0.5 w-6 h-6 border-b-4 border-l-4 border-primary rounded-bl" />
                      <div className="absolute -bottom-0.5 -right-0.5 w-6 h-6 border-b-4 border-r-4 border-primary rounded-br" />
                      {/* Scanning line animation */}
                      <div className="absolute inset-x-2 h-0.5 bg-primary animate-pulse top-1/2 -translate-y-1/2" />
                    </div>
                  </div>
                  <p className="absolute bottom-4 inset-x-0 text-center text-white text-sm bg-black/50 py-1">
                    Position barcode within the frame
                  </p>
                </div>
              )}

              {/* Success Overlay */}
              {scannerStatus === 'success' && (
                <div className="absolute inset-0 bg-green-500/20 flex items-center justify-center">
                  <div className="text-center">
                    <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-2" />
                    <p className="text-white font-bold">{lastScannedCode}</p>
                  </div>
                </div>
              )}
            </div>
            
            <button
              onClick={stopCamera}
              className="w-full py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors font-semibold"
            >
              Stop Scanning
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Error Message */}
            {error && (
              <div className="flex items-start gap-3 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400">
                <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <p className="text-sm">{error}</p>
              </div>
            )}

            {/* Camera Scanner Button */}
            <button
              onClick={startCamera}
              disabled={scannerStatus === 'initializing'}
              className="w-full py-4 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-all flex items-center justify-center gap-3 font-semibold disabled:opacity-50"
            >
              {scannerStatus === 'initializing' ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Initializing Camera...
                </>
              ) : (
                <>
                  <Camera className="w-5 h-5" />
                  Start Camera Scanner
                </>
              )}
            </button>

            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-border" />
              </div>
              <div className="relative flex justify-center">
                <span className="bg-card px-3 text-xs text-muted-foreground uppercase tracking-wider">
                  Or enter manually
                </span>
              </div>
            </div>

            {/* Manual Entry */}
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Keyboard className="w-4 h-4" />
                <span>Type or scan with USB scanner</span>
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={manualBarcode}
                  onChange={(e) => setManualBarcode(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleManualScan()}
                  placeholder="Enter barcode or ISBN..."
                  className="flex-1 px-4 py-3 bg-background/50 border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary font-mono"
                  autoFocus
                  data-barcode-input="true"
                />
                <button
                  onClick={handleManualScan}
                  disabled={manualBarcode.trim().length < 3}
                  className="px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Add
                </button>
              </div>
            </div>

            {/* USB Scanner Hint */}
            <div className="p-3 bg-muted/30 rounded-lg">
              <p className="text-xs text-muted-foreground text-center">
                💡 <span className="font-semibold">USB Scanner:</span> Just scan - the barcode will be detected automatically on the POS screen.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default BarcodeScanner
